import React, { useState, useEffect } from 'react';
import axios from 'axios';

const MasterChefGalleryReview = () => {
  const [chefs, setChefs] = useState([]);
  const [selectedChef, setSelectedChef] = useState(null);
  const [chefProfile, setChefProfile] = useState(null);
  const [cateringProfile, setCateringProfile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadChefs();
  }, []);

  const loadChefs = async () => {
    try {
      const response = await axios.get('/admin/users');
      const chefUsers = response.data.users.filter(u => u.role === 'chef');
      setChefs(chefUsers);
    } catch (error) {
      console.error('Failed to load chefs:', error);
    }
  };

  const viewChefMedia = async (chef) => {
    setSelectedChef(chef);
    setLoading(true);
    
    try {
      // Load chef profile (photos/videos)
      const profileResponse = await axios.get(`/chef/profile/${chef.id}`);
      setChefProfile(profileResponse.data);

      // Load catering profile (menus)
      const cateringResponse = await axios.get(`/catering/chef/profile/${chef.id}`);
      setCateringProfile(cateringResponse.data);
    } catch (error) {
      console.error('Failed to load chef media:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLunchPhotos = () => {
    return chefProfile?.meal_photos?.filter(p => p.meal_type === 'lunch') || [];
  };

  const getDinnerPhotos = () => {
    return chefProfile?.meal_photos?.filter(p => p.meal_type === 'dinner') || [];
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">👨‍🍳 Chef Gallery Review</h2>
        <p className="text-gray-600 mb-6">View all chefs' photos, videos, and catering menus</p>

        {/* Chef List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {chefs.map(chef => (
            <div
              key={chef.id}
              onClick={() => viewChefMedia(chef)}
              className="border-2 border-gray-200 rounded-lg p-4 hover:border-orange-500 cursor-pointer transition"
            >
              <h3 className="font-bold text-lg text-gray-900">{chef.name}</h3>
              <p className="text-sm text-gray-600">{chef.email}</p>
              <button className="mt-3 w-full bg-orange-600 hover:bg-orange-700 text-white py-2 rounded-lg font-semibold text-sm">
                View Media & Menus
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Chef Media Modal */}
      {selectedChef && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
          <div className="min-h-screen px-4 py-8">
            <div className="bg-white rounded-lg max-w-6xl mx-auto p-6">
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">{selectedChef.name}</h2>
                  <p className="text-gray-600">{selectedChef.email}</p>
                </div>
                <button
                  onClick={() => {
                    setSelectedChef(null);
                    setChefProfile(null);
                    setCateringProfile(null);
                  }}
                  className="text-gray-500 hover:text-gray-700 text-3xl"
                >
                  ×
                </button>
              </div>

              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-orange-600 mx-auto"></div>
                  <p className="text-gray-600 mt-4">Loading chef media...</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Profile Photo */}
                  {chefProfile?.profile_photo_url && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-bold text-lg mb-3">Profile Photo</h3>
                      <img
                        src={chefProfile.profile_photo_url}
                        alt="Profile"
                        className="w-48 h-48 object-cover rounded-lg"
                      />
                    </div>
                  )}

                  {/* Lunch Photos */}
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <h3 className="font-bold text-lg mb-3">🌞 Lunch Menu Photos ({getLunchPhotos().length})</h3>
                    {getLunchPhotos().length > 0 ? (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {getLunchPhotos().map((photo, idx) => (
                          <div key={idx} className="relative">
                            <img
                              src={photo.url}
                              alt={photo.dish_name || `Lunch ${idx + 1}`}
                              className="w-full h-40 object-cover rounded-lg"
                            />
                            {photo.dish_name && (
                              <div className="mt-2">
                                <p className="font-semibold text-sm">{photo.dish_name}</p>
                                {photo.ai_description && (
                                  <p className="text-xs text-gray-600">{photo.ai_description.substring(0, 50)}...</p>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500">No lunch photos uploaded</p>
                    )}
                  </div>

                  {/* Dinner Photos */}
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="font-bold text-lg mb-3">🌙 Dinner Menu Photos ({getDinnerPhotos().length})</h3>
                    {getDinnerPhotos().length > 0 ? (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {getDinnerPhotos().map((photo, idx) => (
                          <div key={idx} className="relative">
                            <img
                              src={photo.url}
                              alt={photo.dish_name || `Dinner ${idx + 1}`}
                              className="w-full h-40 object-cover rounded-lg"
                            />
                            {photo.dish_name && (
                              <div className="mt-2">
                                <p className="font-semibold text-sm">{photo.dish_name}</p>
                                {photo.ai_description && (
                                  <p className="text-xs text-gray-600">{photo.ai_description.substring(0, 50)}...</p>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500">No dinner photos uploaded</p>
                    )}
                  </div>

                  {/* Videos */}
                  {chefProfile?.video_urls && chefProfile.video_urls.length > 0 && (
                    <div className="bg-purple-50 rounded-lg p-4">
                      <h3 className="font-bold text-lg mb-3">🎥 Introduction Videos ({chefProfile.video_urls.length})</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {chefProfile.video_urls.map((videoUrl, idx) => (
                          <div key={idx}>
                            <video
                              src={videoUrl}
                              controls
                              className="w-full h-48 rounded-lg bg-black"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Catering Menus */}
                  {cateringProfile?.accepts_catering && (
                    <div className="bg-green-50 rounded-lg p-4">
                      <h3 className="font-bold text-lg mb-3">🍽️ Catering Menus</h3>
                      
                      <div className="space-y-4">
                        {/* Basic Menu */}
                        {cateringProfile.basic_menu && cateringProfile.basic_menu.length > 0 && (
                          <div className="bg-white rounded-lg p-4 border-2 border-blue-200">
                            <h4 className="font-bold text-blue-900 mb-2">Basic - $75/plate (3 courses)</h4>
                            <div className="space-y-2">
                              {cateringProfile.basic_menu.map((item, idx) => (
                                <div key={idx}>
                                  <p className="font-semibold text-sm">Course {item.course_number}: {item.name}</p>
                                  <p className="text-xs text-gray-600">{item.description}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Premium Menu */}
                        {cateringProfile.premium_menu && cateringProfile.premium_menu.length > 0 && (
                          <div className="bg-white rounded-lg p-4 border-2 border-purple-200">
                            <h4 className="font-bold text-purple-900 mb-2">Premium - $100/plate (4 courses)</h4>
                            <div className="space-y-2">
                              {cateringProfile.premium_menu.map((item, idx) => (
                                <div key={idx}>
                                  <p className="font-semibold text-sm">Course {item.course_number}: {item.name}</p>
                                  <p className="text-xs text-gray-600">{item.description}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Deluxe Menu */}
                        {cateringProfile.deluxe_menu && cateringProfile.deluxe_menu.length > 0 && (
                          <div className="bg-white rounded-lg p-4 border-2 border-orange-200">
                            <h4 className="font-bold text-orange-900 mb-2">Deluxe - $150/plate (5 courses)</h4>
                            <div className="space-y-2">
                              {cateringProfile.deluxe_menu.map((item, idx) => (
                                <div key={idx}>
                                  <p className="font-semibold text-sm">Course {item.course_number}: {item.name}</p>
                                  <p className="text-xs text-gray-600">{item.description}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {(!cateringProfile.basic_menu?.length && !cateringProfile.premium_menu?.length && !cateringProfile.deluxe_menu?.length) && (
                          <p className="text-gray-500">No catering menus created yet</p>
                        )}
                      </div>
                    </div>
                  )}

                  {!cateringProfile?.accepts_catering && (
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <p className="text-gray-600">This chef is not accepting catering orders</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterChefGalleryReview;
